'use client';
import { useRouter, useSearchParams } from 'next/navigation';
import Input from '@/components/atoms/Input';
import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { errorsMessage } from '@/libs/errors';
import { notify } from '@/utils/funtion';
import { Providers } from '@/stores/common/providers';
import Button from '@/components/atoms/button';
import { signIn as signInNextAuth } from 'next-auth/react';

interface FormValues {
  email: string;
  password: string;
}

interface SignInProps {
  handleChangeForm?: (value: number) => void;
}

const SignIn: React.FC<SignInProps> = ({ handleChangeForm }) => {
  const searchParams = useSearchParams();
  const prveIdAccess = searchParams?.get('id');
  const callbackUrl = searchParams?.get('callbackUrl');
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const schema = yup.object().shape({
    email: yup
      .string()
      .required('Email là bắt buộc')
      .email('Email không hợp lệ')
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'Tên đăng nhập không chứa kí tự đặc biệt',
      ),
    password: yup
      .string()
      .required('Mật khẩu là bắt buộc')
      .min(6, 'Mật khẩu phải ít nhất 6 ký tự')
      .required('Mật khẩu là bắt buộc'),
  });

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const message = searchParams?.get('message');
    if (!message?.trim() || message === 'undefined') return;
    else {
      notify('error', message, 'top-center', 5000);
    }
  }, []);

  useEffect(() => {
    const expired = searchParams?.get('expired');
    if (!expired?.trim() || expired === 'undefined') return;
    else {
      notify(
        'error',
        'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại!',
        'top-center',
        5000,
      );
    }
  }, []);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setLoading(true);
    await signInNextAuth('credentials', {
      email: data.email,
      password: data.password,
      redirect: false,
    })
      .then(async (response) => {
        if (!response?.ok) {
          const errors = errorsMessage[response?.error as string];
          notify('error', errors, 'top-center', 5000);
        } else {
          const result = await fetch('/api/auth/session').then((res) =>
            res.json(),
          );
          sessionStorage.removeItem('unauthorized');
          notify(
            'info',
            `Xin chào, ${result?.user?.name || result?.user?.full_name}`,
            'top-center',
            4000,
          );

          if (callbackUrl) {
            router.push(decodeURIComponent(callbackUrl));
          } else if (prveIdAccess) {
            router.push(`/?id=${prveIdAccess}`);
          } else {
            router.push('/');
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Providers>
      <div className="t-sign-in">
        <h1>Đăng nhập</h1>
        <form action="POST">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                id="email"
                autoFocus
                placeholder="Nhập email của bạn..."
                label="Email"
                type="default"
                {...field}
                errorMessage={errors.email?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <Input
                id="password"
                label="Mật khẩu"
                placeholder="*************"
                type="password"
                {...field}
                errorMessage={errors.password?.message}
                onPressEnter={handleSubmit(onSubmit)}
              />
            )}
          />

          <div className="t-sign-in_forget">
            <a
              onClick={() => {
                if (handleChangeForm) handleChangeForm(2);
                setError('email', { message: '' });
                setError('password', { message: '' });
              }}
            >
              Bạn quên mật khẩu?
            </a>
          </div>
          <div className="t-sign-in_button">
            <Button
              onClick={handleSubmit(onSubmit)}
              loading={loading}
              shape="fill"
              variant="secondary"
            >
              Đăng nhập
            </Button>
          </div>
          <div className="t-sign-in_signin">
            <span>Bạn chưa có tài khoản?</span>
            <a
              onClick={() => {
                if (handleChangeForm) handleChangeForm(1);
                setError('email', { message: '' });
                setError('password', { message: '' });
              }}
            >
              Đăng ký ngay!
            </a>
          </div>
        </form>
      </div>
    </Providers>
  );
};

export default SignIn;
